import React, { useMemo, useState } from 'react';
import { Modal, Table, Row, Select, Tooltip } from 'antd';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { ExclamationCircleFilled } from '@ant-design/icons';

import OnTraccrButton from '../../common/buttons/OnTraccrButton';
import OnTraccrNumberInput from '../../common/inputs/OnTraccrNumberInput';
import OnTraccrTextInput from '../../common/inputs/OnTraccrTextInput';
import ManualEntryDatePicker from '../../clock/ManualEntry/ManualEntryDatePicker';
import { createWorkReportEntries } from '../../abWorkReporting/state/abWorkReporting.actions';
import CustomConfirmModal from '../../common/modals/CustomConfirmModal';
import {
  getABWorkStatusSelectOptions,
  getFiscalPeriod,
  getStatus,
  isClosedOrHistoricalBucket,
} from './BoardCardABWorkReportingHelpers';
import { updateBucketStatuses } from '../../buckets/state/buckets.actions';
import Colors from '../../constants/Colors';
import { sortByObjProperty } from '../../common/helpers';

const WORK_REPORT_COMMENTS_MAX = 500;

export default function ABWorkReportingEntryCreator({
  visible,
  onCancel,
  cardId,
  itemId,
  bidItems,
  bidItemColumns,
  selectedItem,
  authorizedQuantityField,
  dateField,
}) {
  const dispatch = useDispatch();
  const statuses = useSelector((state) => state.statuses.statuses);
  const entries = useSelector((state) => state.abWorkReporting.entries);

  const [workReportEntries, setWorkReportEntries] = useState([]);
  const [prevItems, setPrevItems] = useState([]);
  const [loading, setLoading] = useState(false);

  if (prevItems !== bidItems) {
    setPrevItems(bidItems);
    const sortedBidItems = [...bidItems].sort(sortByObjProperty('name'));
    setWorkReportEntries(sortedBidItems.map((bidItem) => ({
      ...bidItem,
      quantity: 0,
      dateWorked: null,
      comments: null,
    })));
  }

  const onValueChanged = ({
    id,
    value,
    property,
  }) => {
    setWorkReportEntries(workReportEntries.map((entry) => (
      entry.id === id ? { ...entry, [property]: value } : entry
    )));
  };

  const entryMap = useMemo(() => entries.reduce((acc, entry) => {
    if (!acc[entry.bidItemId]) {
      acc[entry.bidItemId] = 0;
    }
    acc[entry.bidItemId] += entry.quantity;
    return acc;
  }, {}), [entries]) || {};

  const onConfirm = async (
    payload,
    shouldUpdateSelectedItemStatus = false,
    hadErroredRows = false,
  ) => {
    const itemStatuses = statuses[itemId];
    const result = await dispatch(createWorkReportEntries(payload));
    if (result) {
      // If the selected item status should be updated, update it
      // We close it if all bid items are closed
      if (shouldUpdateSelectedItemStatus) {
        const itemClosedStatus = Object.values(itemStatuses || {}).find((s) => s.status === 'Closed');
        if (itemClosedStatus) {
          dispatch(updateBucketStatuses([{ id: selectedItem.id, statusId: itemClosedStatus.id }]));
        }
      }
      if (hadErroredRows) {
        const succeededBidItemIds = new Set(payload.map((p) => p.bidItemId));
        setWorkReportEntries((prev) => prev.filter(
          (entry) => !succeededBidItemIds.has(entry.id),
        ));
        CustomConfirmModal({
          title: 'Warning',
          content: [
            'Some work reports were created successfully. ',
            'One or more work reports were skipped because they contain invalid or restricted values. ',
            'Review the warning icons for details.',
          ].join(''),
          hideCancel: true,
          okText: 'Close',
          onOk: () => {},
        });
      } else {
        onCancel();
      }
    }
    setLoading(false);
  };

  const errorMap = useMemo(() => {
    const map = {};
    workReportEntries.forEach((entry) => {
      const errorMessages = [];
      const relevantStatus = getStatus(entry, statuses);
      if (relevantStatus === 'Historical') {
        errorMessages.push('Historical status is not allowed');
      }
      if (!entry.dateWorked) {
        errorMessages.push('No date worked');
      }
      if (entry.quantity === 0 && relevantStatus !== 'Closed') {
        errorMessages.push('Quantity is 0 and status is not closed');
      }
      if (!dateField) {
        errorMessages.push('No date field');
      }
      if (!entry[dateField]) {
        errorMessages.push('Date field is empty on bid item.');
      }

      const bidItemFiscalPeriod = getFiscalPeriod(entry[dateField]) || 'None';
      const entryFiscalPeriod = getFiscalPeriod(entry.dateWorked, 'yyyy-MM-dd') || 'None';

      if (bidItemFiscalPeriod !== entryFiscalPeriod) {
        errorMessages.push(`Fiscal period does not match bid item and date worked. Bid item: ${bidItemFiscalPeriod}, Date Worked: ${entryFiscalPeriod}`);
      }

      if (errorMessages.length) {
        map[entry.id] = errorMessages.join(', ');
      }
    });
    return map;
  }, [workReportEntries, dateField]);

  const onCreateEntries = async () => {
    setLoading(true);
    const bidItemMap = {};
    selectedItem?.bidItems?.forEach((bidItem) => {
      bidItemMap[bidItem.id] = bidItem.segmentId;
    });

    const payload = [];
    const authorizedQuantityErrors = [];

    const bidItemsToCheck = new Set(
      selectedItem?.bidItems?.filter(
        (bidItem) => !isClosedOrHistoricalBucket(bidItem, statuses),
      ).map((bidItem) => bidItem.id),
    );

    workReportEntries.forEach((entry) => {
      if (errorMap[entry.id]) return;

      payload.push({
        cardId,
        itemId: selectedItem?.id,
        segmentId: bidItemMap[entry.id],
        bidItemId: entry.id,
        quantity: entry.quantity,
        dateWorked: entry.dateWorked,
        comments: entry.comments,
        statusId: entry.statusId,
      });

      const entryStatus = getStatus(entry, statuses);

      if (entryStatus === 'Closed' || entryStatus === 'Historical') {
        bidItemsToCheck.delete(entry.id);
      }

      if (!authorizedQuantityField || entryStatus !== 'Closed') return;
      const {
        response: {
          value: authorizedQuantity = 0,
        } = {},
      } = entry.customData?.fields?.find((field) => field.id === authorizedQuantityField) || {};

      if (authorizedQuantity > (entry.quantity + (entryMap[entry.id] || 0))) {
        authorizedQuantityErrors.push(entry);
      };
    });

    if (!payload.length) {
      return CustomConfirmModal({
        title: 'Warning',
        content: 'No work reports were created. All entries contain invalid or restricted values. Review the warning icons for details and try again.',
        hideCancel: true,
        okText: 'Close',
        onOk: () => setLoading(false),
      });
    }

    if (!authorizedQuantityErrors.length) {
      return onConfirm(
        payload,
        !bidItemsToCheck.size,
        Object.keys(errorMap).length > 0,
      );
    }
    return CustomConfirmModal({
      title: 'Warning',
      content: (
        <div>
          The following items have authorized quantities that are greater than the reported quantity entered and have a closed status:
          <ul>
            {authorizedQuantityErrors.map(({ id, name }) => (
              <li key={id}>{name}</li>
            ))}
          </ul>
          Are you sure you want to create these entries?
        </div>
      ),
      onOk: () => onConfirm(
        payload,
        !bidItemsToCheck.size,
        Object.keys(errorMap).length > 0,
      ),
      onCancel: () => setLoading(false),
    });
  };

  const nativeColumns = [
    {
      title: '',
      dataIndex: 'error',
      width: 50,
      render: (_, record) => {
        if (!errorMap[record.id]) return null;
        return (
          <Tooltip title={errorMap[record.id]}>
            <ExclamationCircleFilled style={{ color: Colors.ONTRACCR_RED }} />
          </Tooltip>
        );
      },
    },
  ];
  const customFieldColumns = [];
  const isSelectedItemClosed = getStatus(selectedItem, statuses) === 'Closed';

  bidItemColumns.forEach((column) => {
    if (column.dataIndex?.startsWith('field-')) {
      customFieldColumns.push(column);
    } else {
      const formattedColumn = { ...column };
      if (column.dataIndex?.includes('statusId')) {
        formattedColumn.width = 150;
        formattedColumn.render = (statusId, record) => {
          const relevantTemplateStatuses = statuses[record.bucketTemplateId];
          const currentLabel = relevantTemplateStatuses?.[statusId]?.status;
          const statusLocked = currentLabel === 'Historical';
          let statusesOptions = getABWorkStatusSelectOptions({
            relevantStatuses: relevantTemplateStatuses || {},
            currentStatusId: statusId,
          }).map((status) => ({
            value: status.id,
            label: status.status,
          }));

          if (isSelectedItemClosed) {
            statusesOptions = statusesOptions.filter((status) => status.label !== 'Open');
          }

          return (
            <Select
              options={statusesOptions}
              value={statusId}
              onChange={(newValue) => onValueChanged({ id: record.id, value: newValue, property: 'statusId' })}
              style={{ width: '100%' }}
              allowClear={!statusLocked}
              disabled={statusLocked}
            />
          );
        };
      }

      nativeColumns.push(formattedColumn);
    }
  });


  const workReportEntryColumns = [
    ...nativeColumns,
    {
      title: 'Segment',
      dataIndex: 'parentSegment',
      width: 150,
      render: (parentSegment) => parentSegment?.name ?? '',
    },
    {
      title: 'Accomplishment',
      dataIndex: 'quantity',
      width: 100,
      render: (quantity, record) => (
        <OnTraccrNumberInput
          value={quantity}
          onChange={(newValue) => onValueChanged({ id: record.id, value: newValue, property: 'quantity' })}
          precision={2}
        />
      ),
    },
    {
      title: 'Date Worked',
      dataIndex: 'dateWorked',
      width: 150,
      render: (dateWorked, record) => (
        <ManualEntryDatePicker
          value={dateWorked}
          onChange={(newValue) => onValueChanged({ id: record.id, value: newValue, property: 'dateWorked' })}
          allowClear
        />
      ),
    },
    {
      title: 'Comments',
      dataIndex: 'comments',
      width: 200,
      render: (comments, record) => (
        <OnTraccrTextInput
          value={comments}
          textarea
          maxLength={WORK_REPORT_COMMENTS_MAX}
          onChange={(e) => onValueChanged({ id: record.id, value: e?.target?.value, property: 'comments' })}
        />
      ),
    },
    ...customFieldColumns,
  ];

  if (!visible) return null;
  return (
    <Modal
      visible={visible}
      onCancel={onCancel}
      title="Create Work Report Entries"
      footer={null}
      maskClosable={false}
      width="85%"
    >
      <div style={{ height: 400, overflow: 'auto', background: 'white', overflowX: 'scroll' }}>
        <Table
          dataSource={workReportEntries}
          columns={workReportEntryColumns}
          rowKey="id"
          size="small"
          pagination={false}
        />
      </div>
      <Row justify="end" style={{ marginTop: 10, marginBottom: 5 }}>
        <OnTraccrButton
          type="cancel"
          title="Cancel"
          onClick={onCancel}
          style={{ marginRight: 10 }}
          loading={loading}
        />
        <OnTraccrButton
          type="primary"
          title="Create"
          onClick={onCreateEntries}
          style={{ marginRight: 10 }}
          loading={loading}
        />
      </Row>
    </Modal>
  );
}

ABWorkReportingEntryCreator.propTypes = {
  visible: PropTypes.bool.isRequired,
  onCancel: PropTypes.func.isRequired,
  cardId: PropTypes.string.isRequired,
  selectedItem: PropTypes.shape({
    id: PropTypes.string.isRequired,
    bidItems: PropTypes.array.isRequired,
  }).isRequired,
  bidItems: PropTypes.array.isRequired,
  bidItemColumns: PropTypes.array.isRequired,
  authorizedQuantityField: PropTypes.string.isRequired,
  dateField: PropTypes.string.isRequired,
};